import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { input, output } from 'zod';

import type { DialogType } from '../../../../@types';
import type { AffiliateCampaign } from '../../../../@types/api';
import { QUERY_KEYS } from '../../../../constants';
import API_ROUTES from '../../../../constants/apiRoutes';
import useMutateData from '../../../../hooks/useMutateData';
import usePermissions from '../../../../hooks/usePermissions';
import { useCampaignDetailsQuery } from '../../../../queries/campaigns';
import { campaignEditFormSchema, type CampaignEditFormData } from '../../../../schema';
import FormModalLayout from '../../../layouts/FormModalLayout';
import CampaignBonusDetails, { getCampaignBonusDetailsState } from './CampaignBonusDetails';
import CampaignFields from './CampaignFields';

type EditCampaignFormProps = DialogType & {
  campaign: AffiliateCampaign;
};

const getDefaultFormData = (campaign: AffiliateCampaign): CampaignEditFormData => ({
  name: campaign.name,
  type: campaign.type,
  code: campaign.code,
  url: campaign.url,
  validityPeriod: campaign.validityPeriod,
  fromTimestamp: campaign.fromTimestamp,
  toTimestamp: campaign.toTimestamp,
  status: campaign.status,
  description: campaign.description ?? '',
});

const EditCampaignForm = ({ campaign, closeModal }: EditCampaignFormProps) => {
  const { updateData } = useMutateData(API_ROUTES.CAMPAIGNS, [QUERY_KEYS.campaigns], 'crm');
  const { isAdmin, isSuperAdmin } = usePermissions();
  const campaignDetailsQuery = useCampaignDetailsQuery(campaign.id);

  const form = useForm<input<typeof campaignEditFormSchema>, unknown, output<typeof campaignEditFormSchema>>({
    defaultValues: getDefaultFormData(campaign),
    resolver: zodResolver(campaignEditFormSchema),
  });
  const { handleSubmit, reset } = form;

  const disableSubmit = isAdmin || isSuperAdmin;
  const bonusDetailsState = getCampaignBonusDetailsState(campaignDetailsQuery);

  useEffect(() => {
    reset(getDefaultFormData(campaign));
  }, [campaign, reset]);

  const handleClose = () => {
    closeModal?.();
    reset(getDefaultFormData(campaign));
  };

  const onFormSubmit = (data: CampaignEditFormData) => {
    if (disableSubmit) return;

    updateData(campaign.id, campaignEditFormSchema.parse(data), handleClose);
  };

  return (
    <FormProvider {...form}>
      <FormModalLayout
        onSave={handleSubmit(onFormSubmit)}
        label='Edit campaign'
        onClose={handleClose}
        isEdit
        showEditTabs={false}
        disableSubmit={disableSubmit}
      >
        <CampaignFields mode='edit'>
          <CampaignBonusDetails state={bonusDetailsState} />
        </CampaignFields>
      </FormModalLayout>
    </FormProvider>
  );
};

export default EditCampaignForm;
